// SHADOW MARKET™ — Order confirmation page (i18n + landing style).
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ShoppingBag, GraduationCap } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n } from "../i18n/shop";
import "./shadow-market.css";

const ShadowMarketConfirmPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { clear } = useCart();
  const { lang } = useLanguage();
  const t = useShopI18n(lang);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) { setLoading(false); setErr("Missing session_id"); return; }
    let alive = true;
    let tries = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/shadow-market/order/${encodeURIComponent(sessionId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await safeJson(res);
        if (!alive) return;
        setOrder(data);
        if (data.status === "PAID") {
          try { clear(); } catch {}
          setLoading(false);
          return;
        }
        tries += 1;
        if (tries < 8) setTimeout(poll, 1500);
        else setLoading(false);
      } catch (e) {
        if (!alive) return;
        setErr(e.message || "Error");
        setLoading(false);
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId, clear]);

  return (
    <div className="shop-root flex min-h-screen flex-col bg-black text-white antialiased" data-testid="shadow-market-confirm-page">
      <Navbar />
      <div style={{ height: 96 }} />

      <main className="mx-auto w-full max-w-2xl flex-1 px-6 py-12 md:px-10">
        <div className="text-center">
          <div className="confirm-check mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-500/[0.12]">
            <Check className="h-8 w-8 text-emerald-300" />
          </div>
          <h1 className="mt-6 text-3xl font-black tracking-tight text-white md:text-4xl">
            {t.confirm_title}
          </h1>
          <p className="mt-2 text-sm text-white/55">{t.confirm_sub}</p>
        </div>

        {loading && (
          <p className="mt-8 text-center text-sm text-white/40">{t.confirm_syncing}</p>
        )}

        {err && (
          <p className="mt-8 text-center text-sm text-rose-300">{err}</p>
        )}

        {order && (
          <div
            className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-sm"
            data-testid="shop-order-summary"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">{t.confirm_order_no}</span>
              <span className="text-sm font-black text-white">{order.order_number}</span>
            </div>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">{t.confirm_status}</span>
              <span
                className="text-[11px] font-black uppercase tracking-[0.18em]"
                style={{ color: order.status === "PAID" ? "#34d399" : "#fbbf24" }}
              >
                {order.status === "PAID" ? t.confirm_paid : t.confirm_processing}
              </span>
            </div>

            <div className="mt-3 space-y-2 border-t border-dashed border-white/10 pt-3 text-sm">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-white/70">
                    {it.name} · {it.color} · {it.size} <span className="text-white/40">×{it.quantity}</span>
                  </span>
                  <span className="text-white">€{(it.unit_price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="mt-3 space-y-1 border-t border-dashed border-white/10 pt-3 text-xs">
              <div className="flex justify-between"><span className="text-white/45">{t.cart_subtotal}</span><span>€{order.subtotal?.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-white/45">{t.cart_shipping}</span><span>{order.shipping === 0 ? t.cart_free : `€${order.shipping?.toFixed(2)}`}</span></div>
              <div className="mt-2 flex justify-between border-t border-dashed border-white/10 pt-2">
                <span className="text-[11px] font-black uppercase tracking-[0.22em] text-white/55">{t.cart_total}</span>
                <span className="text-lg font-black text-white">€{order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/shadow-market" className="shop-btn-ghost" data-testid="shop-confirm-continue">
            <ShoppingBag className="h-3.5 w-3.5" /> {t.confirm_continue}
          </Link>
          <Link to="/academy" className="shop-btn-primary" data-testid="shop-confirm-academy">
            <GraduationCap className="h-3.5 w-3.5" /> {t.confirm_to_academy}
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShadowMarketConfirmPage;
